import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { User, Trophy, Calendar } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  belt_level_id?: string;
  belt_level?: {
    id: string;
    name: string;
    color: string;
    order_sequence: number;
  };
}

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
}

const BeltManagement = () => {
  useUiLanguage();
  const { t } = useTranslation(['admin', 'common']);
  const { profile } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [beltLevels, setBeltLevels] = useState<BeltLevel[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (profile?.dojo_id) {
      fetchStudents();
      fetchBeltLevels();
    }
  }, [profile?.dojo_id]);

  // Listen for belt system changes from the syllabus manager
  useEffect(() => {
    const handleBeltSystemChange = () => {
      console.log('🔄 Belt system changed - refreshing data...');
      fetchBeltLevels();
      fetchStudents();
    };

    window.addEventListener('beltSystemChanged', handleBeltSystemChange);

    return () => {
      window.removeEventListener('beltSystemChanged', handleBeltSystemChange);
    };
  }, []);

  const fetchStudents = async () => {
    if (!profile?.dojo_id) return;

    try {
      console.log('📚 Fetching students for dojo:', profile.dojo_id);

      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          belt_level:belt_levels(id, name, color, order_sequence)
        `)
        .eq('role', 'student')
        .eq('dojo_id', profile.dojo_id)
        .order('first_name');

      if (error) {
        console.error('Error fetching students:', error);
        toast({ title: uiText("Error"), description: uiText("Failed to load students"), variant: "destructive" });
        return;
      }

      console.log(`✅ Fetched ${data?.length || 0} students`);
      setStudents(data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({ title: uiText("Error"), description: uiText("Failed to load students"), variant: "destructive" });
    }
  };

  const fetchBeltLevels = async () => {
    if (!profile?.dojo_id) return;

    try {
      console.log('🥋 Fetching belt levels for dojo:', profile.dojo_id);

      const { data, error } = await supabase
        .from('belt_levels')
        .select('*')
        .eq('dojo_id', profile.dojo_id)
        .order('order_sequence');

      if (error) {
        console.error('Error fetching belt levels:', error);
        return;
      }

      console.log(`✅ Fetched ${data?.length || 0} belt levels`);
      setBeltLevels(data || []);
    } catch (error) {
      console.error('Error fetching belt levels:', error);
    }
  };

  const resetStudentAttendance = async (studentId: string) => {
    try {
      // Delete all existing attendance records for this student
      const { error } = await supabase
        .from('session_attendance')
        .delete()
        .eq('student_id', studentId);

      if (error) {
        console.error('Error resetting attendance:', error);
        throw error;
      }

      console.log('Successfully reset attendance for student:', studentId);
    } catch (error) {
      console.error('Error resetting student attendance:', error);
      throw error;
    }
  };

  const updateStudentBelt = async (studentId: string, beltLevelId: string, studentName: string) => {
    setIsLoading(true);
    try {
      console.log(`🔄 Updating belt for student ${studentName} to belt ID: ${beltLevelId}`);

      // Handle "none" value as null
      const beltId = beltLevelId === "none" ? null : beltLevelId;

      // First update the belt level
      const { error: beltError } = await supabase
        .from('profiles')
        .update({ belt_level_id: beltId })
        .eq('id', studentId);

      if (beltError) {
        console.error('Error updating student belt:', beltError);
        toast({ title: uiText("Error"), description: uiText("Failed to update belt level"), variant: "destructive" });
        return;
      }

      // Then reset their training attendance
      await resetStudentAttendance(studentId);

      console.log(`✅ Successfully updated belt for ${studentName}`);

      toast({
        title: uiText("Success"),
        description: uiText("Updated belt level for {{value0}} and reset their training attendance to start fresh", { value0: studentName })
      });

      await fetchStudents();
    } catch (error) {
      console.error('Error updating student belt:', error);
      toast({ title: uiText("Error"), description: uiText("Failed to update belt level"), variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced belt color mapping with vibrant colors
  const getBeltColorClass = (color: string, beltName: string = '') => {
    const colorStr = color?.toLowerCase() || '';
    const nameStr = beltName?.toLowerCase() || '';

    // Strong, vibrant color mapping based on belt names primarily
    if (nameStr.includes('white')) return 'bg-white text-gray-900 border-2 border-gray-400 font-semibold';
    if (nameStr.includes('orange')) return 'bg-orange-600 text-white font-semibold';
    if (nameStr.includes('blue')) return 'bg-blue-600 text-white font-semibold';
    if (nameStr.includes('yellow')) return 'bg-yellow-500 text-gray-900 font-semibold';
    if (nameStr.includes('green')) return 'bg-green-600 text-white font-semibold';
    if (nameStr.includes('purple')) return 'bg-purple-600 text-white font-semibold';
    if (nameStr.includes('brown')) return 'bg-amber-800 text-white font-semibold';
    if (nameStr.includes('red')) return 'bg-red-600 text-white font-semibold';
    if (nameStr.includes('black')) return 'bg-gray-900 text-white font-semibold';

    // Fallback to color field
    if (colorStr.includes('white')) return 'bg-white text-gray-900 border-2 border-gray-400 font-semibold';
    if (colorStr.includes('orange')) return 'bg-orange-600 text-white font-semibold';
    if (colorStr.includes('blue')) return 'bg-blue-600 text-white font-semibold';
    if (colorStr.includes('yellow')) return 'bg-yellow-500 text-gray-900 font-semibold';
    if (colorStr.includes('green')) return 'bg-green-600 text-white font-semibold';
    if (colorStr.includes('purple')) return 'bg-purple-600 text-white font-semibold';
    if (colorStr.includes('brown') || colorStr.includes('amber')) return 'bg-amber-800 text-white font-semibold';
    if (colorStr.includes('red')) return 'bg-red-600 text-white font-semibold';
    if (colorStr.includes('black')) return 'bg-gray-900 text-white font-semibold';

    return 'bg-gray-600 text-white font-semibold';
  };

  const getBeltColorForDropdown = (beltLevel: BeltLevel) => {
    const name = beltLevel.name?.toLowerCase() || '';
    const color = beltLevel.color?.toLowerCase() || '';

    // Bright, visible colors for dropdown dots based on belt name primarily
    if (name.includes('white')) return 'bg-white border-2 border-gray-400';
    if (name.includes('orange')) return 'bg-orange-600';
    if (name.includes('blue')) return 'bg-blue-600';
    if (name.includes('yellow')) return 'bg-yellow-500';
    if (name.includes('green')) return 'bg-green-600';
    if (name.includes('purple')) return 'bg-purple-600';
    if (name.includes('brown')) return 'bg-amber-800';
    if (name.includes('red')) return 'bg-red-600';
    if (name.includes('black')) return 'bg-gray-900';

    // Fallback to color field
    if (color.includes('white')) return 'bg-white border-2 border-gray-400';
    if (color.includes('orange')) return 'bg-orange-600';
    if (color.includes('blue')) return 'bg-blue-600';
    if (color.includes('yellow')) return 'bg-yellow-500';
    if (color.includes('green')) return 'bg-green-600';
    if (color.includes('purple')) return 'bg-purple-600';
    if (color.includes('brown') || color.includes('amber')) return 'bg-amber-800';
    if (color.includes('red')) return 'bg-red-600';
    if (color.includes('black')) return 'bg-gray-900';

    return 'bg-gray-600';
  };

  return (
    <Card className="w-full max-w-7xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-center justify-center">
          <Trophy className="h-5 w-5" />
          {uiText("Belt Management")}</CardTitle>
        <CardDescription className="text-center">
          {uiText("Assign and manage belt levels for students. When you change a student's belt, their training attendance will reset to start fresh for the new level.")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">


        {beltLevels.length === 0 ? (
          <div className="text-center py-8 text-amber-600 bg-amber-50 rounded border border-amber-200">
            <Trophy className="h-8 w-8 mx-auto mb-2" />
            <p className="font-medium">{uiText("No Belt System Found")}</p>
            <p className="text-sm">{uiText("Please create a belt system in the Syllabus tab first.")}</p>
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {uiText("No students found.")}</div>
        ) : (
          students.map((student) => {
            // Get the current belt color with improved logic
            let currentBeltColorClass = 'bg-gray-100 text-gray-900 border-2 border-gray-400';
            if (student.belt_level) {
              currentBeltColorClass = getBeltColorClass(student.belt_level.color, student.belt_level.name);
            }

            return (
              <div key={student.id} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors mx-auto w-full max-w-3xl">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="bg-red-100 dark:bg-red-900 p-2 rounded-full flex-shrink-0">
                    <User className="h-4 w-4 text-red-700 dark:text-red-300" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {student.first_name} {student.last_name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {student.email}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <div className="text-left sm:text-right">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{uiText("Current Belt")}</p>
                    {student.belt_level ? (
                      <Badge className={`${currentBeltColorClass} text-sm px-3 py-1 rounded-full whitespace-nowrap`}>
                        {student.belt_level.name}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-sm px-3 py-1 rounded-full">
                        {uiText("No Belt Assigned")}</Badge>
                    )}
                  </div>

                  <div className="w-full sm:w-80">
                    <Select
                      value={student.belt_level_id || 'none'}
                      onValueChange={(value) => updateStudentBelt(student.id, value, `${student.first_name} ${student.last_name}`)}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-full h-10 border-2 hover:border-gray-400 focus:border-blue-500 transition-colors">
                        <SelectValue placeholder={uiText("Select belt level")} />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 shadow-xl z-50 max-h-80">
                        <SelectItem value="none" className="hover:bg-gray-100 dark:hover:bg-gray-700 py-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-4 h-4 bg-gray-300 rounded-full border border-gray-400" />
                            <span className="text-gray-500 font-medium">{uiText("No Belt Assigned")}</span>
                          </div>
                        </SelectItem>
                        {beltLevels.map((belt) => {
                          const dropdownColorClass = getBeltColorForDropdown(belt);
                          console.log(`🎨 Belt: "${belt.name}" -> Color: "${dropdownColorClass}"`);

                          return (
                            <SelectItem key={belt.id} value={belt.id} className="hover:bg-gray-100 dark:hover:bg-gray-700 py-3">
                              <div className="flex items-center space-x-3">
                                <div className={`w-4 h-4 rounded-full ${dropdownColorClass} flex-shrink-0 shadow-sm`} />
                                <span className="font-medium">{belt.name}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default BeltManagement;